import { z } from 'zod';
import {
  getVocabularyValues,
  VOCABULARY_APP_SLOT_MAPS,
} from '@erikdakoda/database/shared/vocabularyRegistry';
import { seedUviloVocabulary } from './seedUviloVocabulary';

/** App whose slot map and vocabulary back factoid analysis and tool schemas. */
export const UVILO_VOCABULARY_APP = 'uvilo-ai';

/**
 * Returns the uvilo-ai namespace bound to Factoid.category1.
 * @returns Vocabulary namespace (e.g. `factoid-category`)
 */
export function uviloCategory1Namespace(): string {
  return VOCABULARY_APP_SLOT_MAPS[UVILO_VOCABULARY_APP]?.category1 ?? 'factoid-category';
}

/**
 * Returns the uvilo-ai namespace bound to Factoid.category2.
 * @returns Vocabulary namespace (e.g. `life-domain`)
 */
export function uviloCategory2Namespace(): string {
  return VOCABULARY_APP_SLOT_MAPS[UVILO_VOCABULARY_APP]?.category2 ?? 'life-domain';
}

/**
 * Returns registered values for a uvilo-ai vocabulary namespace.
 * Seeds uvilo vocabulary when the registry is still empty (module-load builders).
 * @param namespace Vocabulary namespace for the app slot
 * @returns Registered values in registration order
 * @throws When the namespace is still empty after seeding
 */
export function getUviloVocabularyValues(namespace: string): readonly string[] {
  let registered = getVocabularyValues(UVILO_VOCABULARY_APP, namespace);
  if (registered.length === 0) {
    seedUviloVocabulary();
    registered = getVocabularyValues(UVILO_VOCABULARY_APP, namespace);
  }
  if (registered.length === 0) {
    throw new Error(
      `Vocabulary ${UVILO_VOCABULARY_APP}.${namespace} is empty after seeding; check vocabulary registration`,
    );
  }
  return registered;
}

/**
 * Formats registered vocabulary values as a comma-separated list for schema and tool describes.
 * @param namespace Vocabulary namespace for the app slot
 * @returns Comma-separated values
 */
export function formatUviloVocabularyValues(namespace: string): string {
  return getUviloVocabularyValues(namespace).join(', ');
}

/**
 * Builds a zod enum from the vocabulary registry for the given namespace.
 * @param namespace Vocabulary namespace for the app slot
 * @returns Zod enum schema
 */
export function uviloVocabularyEnum(namespace: string) {
  return z.enum(getUviloVocabularyValues(namespace) as [string, ...string[]]);
}

/**
 * Builds the category1 (fact type) enum from the uvilo-ai factoid-category vocabulary.
 * @returns Zod enum schema
 */
export function factoidCategory1Schema() {
  return uviloVocabularyEnum(uviloCategory1Namespace());
}

/**
 * Builds the category2 enum from the uvilo-ai category2 vocabulary slot.
 * @returns Zod enum schema
 */
export function factoidCategory2Schema() {
  return uviloVocabularyEnum(uviloCategory2Namespace());
}

export const ProfileFieldFromAnalysisSchema = z.object({
  ownerId: z.string(),
  profileField: z
    .string()
    .describe('The name of the User Profile field where this fact should be stored'),
  currentValue: z
    .string()
    .nullable()
    .describe('The current value of the given profile field, if it exists, otherwise use null'),
  newValue: z
    .string()
    .describe(
      'The new value that should be stored in the given profile field, formatted to conform to the field type',
    ),
});

/**
 * Builds the factoid-from-analysis schema using live vocabulary registry values.
 * Call after `seedUviloVocabulary()` so enums match the registry.
 * @returns Zod object schema for analyzer-emitted factoids
 */
export function buildFactoidFromAnalysisSchema() {
  return z.object({
    ownerId: z.string(),
    convoId: z
      .string()
      .nullable()
      .describe(
        'Conversation this fact came from; null when the fact is not tied to a conversation',
      ),
    provenanceId: z
      .string()
      .nullable()
      .describe(
        'OwnedItem id of the source user message (AiMessage) or quiz question result that this factoid came from; null for convo-level facts with no message',
      ),
    category1: factoidCategory1Schema().describe(
      'What kind of fact; values from the app factoid-category vocabulary',
    ),
    category2: factoidCategory2Schema().describe('Which area the fact concerns'),
    text: z.string().describe('Full out-of-context sentence starting with the user nickname'),
    date: z.iso.date().describe('YYYY-MM-DD; the fact date, else convo close date'),
  });
}
